import React, { useRef, useCallback } from 'react';
import { usePanelStore } from '../../store/panelStore';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import { getProjectRoot } from '../../services/FileSystemService';
import { useScreenCapture } from '../../hooks/useScreenCapture';
import { useSlotCoreDevServer } from '../../hooks/useSlotCoreDevServer';
import { Panel } from '../panels/Panel';
import { ViewportToolbar } from './ViewportToolbar';
import { GameViewport } from './GameViewport';
import { IframeGameViewport } from './IframeGameViewport';
import { IframeEmptyState } from './IframeEmptyState';
import { ViewportStatusBar } from './ViewportStatusBar';
import { PlayModeControls } from './PlayModeControls';
import { DebugOverlay } from './DebugOverlay';
import { ScreenshotTool } from './ScreenshotTool';
import { RecordingTool } from './RecordingTool';

export const LivePreviewPanel: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { panels } = usePanelStore();
  const panelConfig = panels['live-preview'];
  const previewMode = useLivePreviewStore(s => s.previewMode);
  const { isRunning, isStarting, error, checkHealth, startServer } = useSlotCoreDevServer(previewMode === 'iframe');

  const {
    isCapturing,
    isRecording,
    isPaused,
    takeScreenshot,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
  } = useScreenCapture({ canvasRef });

  if (!panelConfig?.visible) return null;

  const renderContent = () => {
    if (previewMode === 'iframe') {
      if (isRunning) {
        return <IframeGameViewport className="w-full h-full" />;
      }
      return (
        <IframeEmptyState
          error={error}
          isStarting={isStarting}
          onRetry={checkHealth}
          onStartServer={() => {
            // Pass the project folder name — ai-proxy resolves the full path
            const rootHandle = getProjectRoot();
            const projectName = rootHandle?.name ?? 'slot-project';
            startServer(projectName);
          }}
        />
      );
    }
    return (
      <>
        <GameViewport className="w-full h-full" />
        <DebugOverlay />
      </>
    );
  };

  return (
    <Panel panel={panelConfig} className="flex-1 h-full">
      <div className="flex flex-col h-full">
        {/* Top toolbar */}
        <div className="flex items-center justify-between px-2 py-1 bg-zinc-800 border-b border-zinc-700">
          <PlayModeControls />

          <div className="flex items-center gap-2">
            <ScreenshotTool
              onTakeScreenshot={takeScreenshot}
              isCapturing={isCapturing}
            />
            <RecordingTool
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onPauseRecording={pauseRecording}
              onResumeRecording={resumeRecording}
              isRecording={isRecording}
              isPaused={isPaused}
            />
          </div>
        </div>

        {/* Device/zoom toolbar with Debug, Stats, Input dropdowns */}
        <ViewportToolbar />

        {/* Main content area - full width game viewport */}
        <div className="flex-1 relative overflow-hidden bg-black">
          {renderContent()}
        </div>

        {/* Status bar */}
        <ViewportStatusBar />
      </div>
    </Panel>
  );
};
